import { RouterLink } from '@/common/components/router-links/RouterLink';
import { Trans } from '@lingui/react/macro';
import { ArrowLeft, Mail } from 'lucide-react';
import { z } from 'zod';
import { useState } from 'react';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { requiredInput } from '@/common/form/requiredInput';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { Button } from '@/common/components/ui/button';
import { AccountService } from '@/features/auth/services/AccountService';

const forgotPasswordSchema = z.object({
  email: requiredInput(z.email()),
});

export const ForgotPasswordPage = () => {
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const form = useAppForm(
    appFormOptions({
      schema: forgotPasswordSchema,
      defaultValues: { email: null },
      onSubmit: async ({ value }) => {
        await AccountService.requestPasswordReset({ email: value.email });
        setSubmittedEmail(value.email);
      },
    })
  );

  if (submittedEmail !== null) {
    return (
      <div className="flex flex-col items-center text-center">
        <div className="mb-6 flex size-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
          <Mail size={24} />
        </div>
        <h1 className="mb-2 text-2xl font-bold tracking-tight text-gray-900">
          <Trans>Check your email</Trans>
        </h1>
        <p className="mb-8 text-sm text-gray-500">
          <Trans>
            We sent a password reset link to{' '}
            <span className="font-medium text-gray-900">{submittedEmail}</span>
          </Trans>
        </p>

        <RouterLink to="/login" className="w-full">
          <Button className="w-full">
            <Trans>Back to sign in</Trans>
          </Button>
        </RouterLink>

        <p className="mt-6 text-sm text-gray-500">
          <Trans>Didn&apos;t receive the email?</Trans>{' '}
          <button
            type="button"
            className="font-semibold text-blue-600 transition-colors hover:text-blue-500"
            onClick={() => setSubmittedEmail(null)}
          >
            <Trans>Send again</Trans>
          </button>
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-8 flex flex-col items-center gap-2">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          <Trans>Forgot password?</Trans>
        </h1>
        <p className="text-center text-sm text-gray-500">
          <Trans>No worries, we&apos;ll send you reset instructions.</Trans>
        </p>
      </div>

      <form.AppForm>
        <form.FormElement>
          <FormErrors className="mb-4" />

          <form.AppField name="email">
            {(field) => (
              <field.TextField
                label={<Trans>Email</Trans>}
                type="email"
                placeholder="name@company.com"
              />
            )}
          </form.AppField>

          <FormSubmit className="mt-6 w-full">
            <Trans>Reset password</Trans>
          </FormSubmit>
        </form.FormElement>
      </form.AppForm>

      <div className="mt-8 text-center text-sm">
        <RouterLink
          to="/login"
          className="inline-flex items-center font-semibold text-gray-600 transition-colors hover:text-gray-900"
        >
          <ArrowLeft size={16} className="mr-2" />
          <Trans>Back to sign in</Trans>
        </RouterLink>
      </div>
    </>
  );
};
